import React, { useState } from "react";
import { AdminContainer } from "../../components/layouts";
import MaterialTable from "material-table";
import Time from "../../lithic/Moment";
import api from "../../api";
import {
  apiCollectionToRowResponse,
  rowQueryToApiParams,
} from "../../modules/tableQuery";
import tableIcons from "../../components/tableIcons";
import Handsontable from "handsontable";
import dayjs from "dayjs";
import { Button, CircularProgress, Snackbar, Select, MenuItem } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import theme from "../../theme";
import { makeStyles } from "@material-ui/core/styles";

function dateRenderer(instance, td, row, col, prop, value, cellProperties) {
  Handsontable.renderers.TextRenderer.apply(this, arguments);
  td.innerHTML = value ? dayjs(value).format("MMM DD, YYYY") : "";
  td.style.fontSize = theme.typography.fontSizeTable;
  return td;
}

const methodValues = [
  "sync_applicants",
  "sync_properties",
  "sync_units",
  "sync_partner",
  "sync_enrollments",
  "sync_enrollments_request",
  "post_rent_reporting_charges",
  "post_pms_charge",
  "remove_pms_charge",
  "save_application_external_id",
  "save_applicants_external_ids",
  "get_units_sync_status",
  "get_properties_sync_status",
  "get_partner_sync_status",
  "create_pms_info",
  "update_pms_info",
  "fetch_pms_info",
  "fetch_pms_transactions",
  "fetch_outstanding_balances",
  "fetch_rently_units",
];

const pmsTypeValues = [
  "appfolio",
  "buildium",
  "entrata",
  "manage_america",
  "propertyware",
  "quext",
  "rent_manager",
  "renters_warehouse",
  "rentvine",
  "resman",
  "revela",
  "yardi",
];

const statusLookup = {
  in_progress: "In Progress",
  done: "Done",
  failed: "Failed",
  pending: "Pending",
  finished_with_errors: "Finished with Errors",
};

// Define custom styles using makeStyles
const useStyles = makeStyles({
  filterMenuPaper: {
    maxHeight: "none !important",
    height: "auto !important",
  },
  detailPanel: {
    padding: 20,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  },
  detailTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  detailLabel: {
    padding: "8px",
    fontWeight: "bold",
    borderBottom: "1px solid #ddd",
  },
  detailValue: {
    padding: "8px",
    borderBottom: "1px solid #ddd",
  },
  sectionWrapper: {
    marginTop: "20px",
  },
  codeBlock: {
    whiteSpace: "pre-wrap",
    maxHeight: 400,
    overflow: "auto",
    padding: "10px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
  },
  smallCodeBlock: {
    whiteSpace: "pre-wrap",
    maxHeight: 200,
    overflow: "auto",
    padding: "10px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
  },
});

const PmsRequestLogsPage = () => {
  const tableRef = React.createRef();
  const [loading, setLoading] = useState({});
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const classes = useStyles(); // Initialize styles

  const handleRerunRequest = async (id) => {
    if (loading[id]) return;

    // Set loading state for this specific button
    setLoading((prev) => ({ ...prev, [id]: true }));

    try {
      // Make API request to rerun the request with the specific ID
      await api.rerunPmsRequest({ id });

      // Handle successful response
      setAlert({
        open: true,
        message: `Request ${id} has been rerun successfully`,
        severity: "success",
      });

      // Refresh the table data
      tableRef.current && tableRef.current.onQueryChange();
    } catch (error) {
      setAlert({
        open: true,
        message: `Error rerunning request: ${error.message || "Unknown error"}`,
        severity: "error",
      });
    } finally {
      // Clear loading state
      setLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const makeColumns = () => [
    {
      title: "ID",
      field: "id",
      filtering: true,
    },
    {
      title: "Date",
      field: "createdAt",
      type: "datetime",
      defaultSort: "desc",
      filtering: false,
      renderer: dateRenderer,
    },
    {
      title: "Method",
      field: "kind",
      filtering: true,
      filterComponent: (props) => {
        const filterValue = props.columnDef.tableData.filterValue || "";
        return (
          <Select
            value={filterValue}
            onChange={(e) =>
              props.onFilterChanged(props.columnDef.tableData.id, e.target.value)
            }
            style={{ width: "100%" }}
            displayEmpty
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              PaperProps: {
                className: classes.filterMenuPaper,
              },
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {methodValues.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      title: "PMS Type",
      field: "pmsType",
      filtering: true,
      filterComponent: (props) => {
        const filterValue = props.columnDef.tableData.filterValue || "";
        return (
          <Select
            value={filterValue}
            onChange={(e) =>
              props.onFilterChanged(props.columnDef.tableData.id, e.target.value)
            }
            style={{ width: "100%" }}
            displayEmpty
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              getContentAnchorEl: null,
              PaperProps: {
                className: classes.filterMenuPaper,
              },
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {pmsTypeValues.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Partner ID",
      field: "partnerId",
      filtering: true,
    },
    {
      title: "Subject Type",
      field: "subjectType",
      filtering: true,
    },
    {
      title: "Subject ID",
      field: "subjectId",
      filtering: true,
    },
    {
      title: "Status",
      field: "status",
      filtering: true,
      lookup: statusLookup,
      filterComponent: (props) => {
        const filterValue = props.columnDef.tableData.filterValue || "";
        return (
          <Select
            value={filterValue}
            onChange={(e) =>
              props.onFilterChanged(props.columnDef.tableData.id, e.target.value)
            }
            style={{ width: "100%" }}
            displayEmpty
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              PaperProps: {
                className: classes.filterMenuPaper,
              },
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="done">Done</MenuItem>
            <MenuItem value="finished_with_errors">Finished with Errors</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        );
      },
    },
    {
      title: "Actions",
      field: "actions",
      filtering: false,
      sorting: false,
      searchable: false,
      render: (rowData) => {
        if (rowData.eligibleForRerun && rowData.status === "failed") {
          return (
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={loading[rowData.id]}
              onClick={() => handleRerunRequest(rowData.id)}
              style={{ minWidth: "80px" }}
            >
              {loading[rowData.id] ? <CircularProgress size={20} /> : "Rerun"}
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <AdminContainer variant="list">
      <MaterialTable
        title="PMS Request Logs"
        tableRef={tableRef}
        columns={makeColumns()}
        options={{
          debounceInterval: 500,
          emptyRowsWhenPaging: false,
          filtering: true,
          pageSize: 50,
          pageSizeOptions: [50, 100, 200],
          sorting: true,
          thirdSortClick: false,
          filterCellStyle: {
            transform: "scale(0.80)",
            transformOrigin: "left",
            maxWidth: 0,
            paddingTop: "4px",
            paddingBottom: "4px",
          },
        }}
        data={(q) => {
          const params = rowQueryToApiParams(q);

          return api
            .getPmsRequestLogs(params)
            .then((resp) => {
              try {
                if (resp.data && Array.isArray(resp.data)) {
                  return {
                    data: resp.data.map((item) => ({
                      ...item,
                      key: item.id,
                    })),
                    page: params.page - 1 || 0,
                    totalCount: resp.data.length,
                  };
                }

                // Return data using items array if it exists
                if (resp.data && resp.data.items) {
                  return {
                    data: resp.data.items,
                    page: resp.data.currentPage - 1 || 0,
                    totalCount: resp.data.totalCount || 0,
                  };
                }

                return apiCollectionToRowResponse(resp);
              } catch (e) {
                return { data: [], page: 0, totalCount: 0 };
              }
            })
            .catch((err) => {
              return { data: [], page: 0, totalCount: 0 };
            });
        }}
        detailPanel={(rowData) => {
          if (!rowData) return null;

          const formatJson = (data) => {
            if (!data) return "None";
            try {
              if (typeof data === "object") {
                return JSON.stringify(data, null, 2);
              }

              if (typeof data === "string") {
                return JSON.stringify(JSON.parse(data), null, 2);
              }
            } catch (e) {
              return data;
            }
          };

          return (
            <div className={classes.detailPanel}>
              <div className={classes.detailGrid}>
                <div>
                  <h3>Log Information</h3>
                  <table className={classes.detailTable}>
                    <tbody>
                      <tr>
                        <td className={classes.detailLabel}>ID:</td>
                        <td className={classes.detailValue}>{rowData.id || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Partner ID:</td>
                        <td className={classes.detailValue}>
                          {rowData.partnerId || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Kind:</td>
                        <td className={classes.detailValue}>{rowData.kind || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>PMS_TYPE:</td>
                        <td className={classes.detailValue}>{rowData.pmsType || "--"}</td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Subject Type:</td>
                        <td className={classes.detailValue}>
                          {rowData.subjectType || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Subject ID:</td>
                        <td className={classes.detailValue}>
                          {rowData.subjectId || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Processed By ID:</td>
                        <td className={classes.detailValue}>
                          {rowData.processedById || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Processed By Type:</td>
                        <td className={classes.detailValue}>
                          {rowData.processedByType || "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3>Status Information</h3>
                  <table className={classes.detailTable}>
                    <tbody>
                      <tr>
                        <td className={classes.detailLabel}>Status:</td>
                        <td className={classes.detailValue}>{rowData.status}</td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Created At:</td>
                        <td className={classes.detailValue}>
                          <Time format={"MMM DD, YYYY HH:mm:ss"}>
                            {rowData.createdAt}
                          </Time>
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.detailLabel}>Updated At:</td>
                        <td className={classes.detailValue}>
                          <Time format={"MMM DD, YYYY HH:mm:ss"}>
                            {rowData.createdAt}
                          </Time>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={classes.sectionWrapper}>
                <h3>Options</h3>
                <pre className={classes.smallCodeBlock}>
                  {formatJson(rowData.options)}
                </pre>
              </div>

              <div className={classes.sectionWrapper}>
                <h3>Response</h3>
                <pre className={classes.codeBlock}>{formatJson(rowData.response)}</pre>
              </div>
            </div>
          );
        }}
        icons={tableIcons}
      />

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </AdminContainer>
  );
};

export default PmsRequestLogsPage;
